import { Link } from "react-router-dom";
import { Users, AlertCircle, CheckCircle, FileText, Shield, ChevronRight, Clock } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from '../components/SEO';

const SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  'headline': 'Mobbing beim Betriebsrat melden: Rechte, Pflichten und erste Schritte',
  'description': 'Was muss der Betriebsrat bei Mobbing tun? Welche Rechte haben Betroffene? Schritt-für-Schritt-Anleitung für Arbeitnehmer in Deutschland.',
  'author': { '@type': 'Organization', 'name': 'mobbing-melden.de' },
  'publisher': { '@type': 'Organization', 'name': 'mobbing-melden.de', 'url': 'https://www.mobbing-melden.de' },
  'inLanguage': 'de-DE',
};

const RIGHTS = [
  { icon: "📋", title: "Beschwerderecht (§ 84 BetrVG)", text: "Jeder Arbeitnehmer hat das Recht, sich beim Betriebsrat über Mobbing durch Kollegen oder Vorgesetzte zu beschweren — ohne Nachteile befürchten zu müssen." },
  { icon: "🔍", title: "Untersuchungspflicht", text: "Der Betriebsrat ist verpflichtet, jede Beschwerde ernst zu nehmen, zu prüfen und dem Arbeitnehmer das Ergebnis mitzuteilen." },
  { icon: "🤝", title: "Vermittlung & Einigungsversuch", text: "Der Betriebsrat kann zwischen Betroffenen und Arbeitgeber vermitteln und auf eine Lösung hinwirken." },
  { icon: "⚖️", title: "Mitbestimmung bei Maßnahmen", text: "Bei Abmahnungen, Versetzungen oder Kündigungen des Mobbers hat der Betriebsrat ein Mitbestimmungsrecht (§ 102 BetrVG)." },
];

const STEPS = [
  {
    number: 1,
    color: "#A0E8AF",
    title: "Dokumentation vorbereiten",
    text: "Bevor du zum Betriebsrat gehst, führe mindestens 2–4 Wochen ein lückenloses Protokoll mit Datum, Ort, Beteiligten und konkreten Aussagen.",
    link: { label: "Mobbing-Protokoll erstellen →", path: "/mobbing-protokoll" }
  },
  {
    number: 2,
    color: "#BDE0FE",
    title: "Termin beim Betriebsrat anfragen",
    text: "Wende dich direkt an den Betriebsratsvorsitzenden oder ein Betriebsratsmitglied deines Vertrauens. Das Gespräch ist vertraulich.",
    link: null
  },
  {
    number: 3,
    color: "#FFC4D6",
    title: "Beschwerde schriftlich einreichen",
    text: "Eine schriftliche Beschwerde ist wirksamer als ein mündliches Gespräch. Beschreibe die Vorfälle sachlich, chronologisch und mit Belegen.",
    link: { label: "Anonym vorher dokumentieren →", path: "/melden" }
  },
  {
    number: 4,
    color: "#F6E05E",
    title: "Ergebnis abwarten & nachhaken",
    text: "Der Betriebsrat muss dir das Ergebnis seiner Prüfung mitteilen. Setzt er keine Maßnahmen, kannst du Einigungsstelle oder Arbeitsgericht einschalten.",
    link: null
  },
  {
    number: 5,
    color: "#A0E8AF",
    title: "Eskalation wenn nötig",
    text: "Hilft der Betriebsrat nicht ausreichend, sind Arbeitsrechtler, Gewerkschaft oder das Arbeitsgericht die nächsten Schritte.",
    link: { label: "Rechtliche Schritte →", path: "/anwalt" }
  },
];

const DONTS = [
  "Nur mündlich beschweren (immer schriftlich!)",
  "Ohne Dokumentation zum Betriebsrat gehen",
  "Ergebnisse nicht schriftlich bestätigen lassen",
  "Zu lange warten — Verjährungsfristen beachten",
  "Den Arbeitgeber gleichzeitig informieren, bevor der BR tätig war",
];

export default function Betriebsrat() {
  return (
    <div className="min-h-screen bg-[#FFFDF9]">
      <Header />
      <SEO
        title="Mobbing beim Betriebsrat melden: Rechte & Anleitung 2025"
        description="Was muss der Betriebsrat bei Mobbing tun? Welche Rechte haben Sie? Schritt-für-Schritt-Anleitung für Arbeitnehmer + kostenlose anonyme Meldestelle."
        path="/betriebsrat"
        schema={SCHEMA}
      />

      <main className="px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-4xl mx-auto">

          {/* Hero */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-[#A0E8AF]/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <Users className="w-10 h-10 text-[#1A4731]" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#2D3748] mb-4" style={{ fontFamily: 'Nunito' }}>
              Mobbing beim Betriebsrat melden
            </h1>
            <p className="text-lg text-[#718096] max-w-2xl mx-auto">
              Der Betriebsrat hat klare Pflichten bei Mobbing. Hier erfährst du, welche Rechte du hast, was du vorbereiten musst — und was passiert, wenn der Betriebsrat nicht handelt.
            </p>
          </div>

          {/* Key info box */}
          <div className="bg-[#E6FFFA] border border-[#A0E8AF] rounded-2xl p-6 mb-10 flex gap-4">
            <CheckCircle className="w-6 h-6 text-[#1A4731] flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="font-bold text-[#1A4731] mb-1">Das Wichtigste vorab</h2>
              <p className="text-[#4A5568] text-sm">
                Du hast ein <strong>gesetzliches Beschwerderecht</strong> nach § 84 BetrVG. Der Betriebsrat muss deine Beschwerde prüfen und dir eine Rückmeldung geben. Das Gespräch ist <strong>vertraulich</strong> — dein Arbeitgeber wird nicht automatisch informiert.
              </p>
            </div>
          </div>

          {/* Rights */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-6" style={{ fontFamily: 'Nunito' }}>
              Deine Rechte — was der Betriebsrat tun muss
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {RIGHTS.map((r, i) => (
                <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5">
                  <div className="text-2xl mb-2">{r.icon}</div>
                  <h3 className="font-bold text-[#2D3748] mb-2 text-sm">{r.title}</h3>
                  <p className="text-[#718096] text-sm">{r.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Steps */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-6" style={{ fontFamily: 'Nunito' }}>
              Schritt für Schritt: So gehst du vor
            </h2>
            <div className="space-y-4">
              {STEPS.map((step, i) => (
                <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5 flex gap-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-white" style={{ backgroundColor: step.color }}>
                    {step.number}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-[#2D3748] mb-1">{step.title}</h3>
                    <p className="text-[#718096] text-sm mb-2">{step.text}</p>
                    {step.link && (
                      <Link to={step.link.path} className="inline-flex items-center gap-1 text-[#1A4731] text-xs font-bold hover:underline">
                        {step.link.label}
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* What NOT to do */}
          <div className="bg-[#FFF5F7] border border-[#FFC4D6] rounded-2xl p-6 mb-10">
            <h2 className="text-xl font-bold text-[#2D3748] mb-4 flex items-center gap-2" style={{ fontFamily: 'Nunito' }}>
              <AlertCircle className="w-5 h-5 text-[#C53030]" />
              Diese Fehler unbedingt vermeiden
            </h2>
            <ul className="space-y-2">
              {DONTS.map((d, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[#4A5568]">
                  <span className="text-[#C53030] font-bold flex-shrink-0">✗</span>
                  {d}
                </li>
              ))}
            </ul>
          </div>

          {/* What if BR doesn't help */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-10">
            <h2 className="text-xl font-bold text-[#2D3748] mb-4" style={{ fontFamily: 'Nunito' }}>
              Was, wenn der Betriebsrat nicht hilft?
            </h2>
            <div className="space-y-3 text-sm text-[#718096]">
              <p>Wenn der Betriebsrat untätig bleibt oder keine Lösung zustande kommt, hast du weitere Optionen:</p>
              <div className="grid md:grid-cols-3 gap-3 mt-4">
                {[
                  { emoji: "⚖️", label: "Arbeitsgericht", desc: "Klage auf Unterlassung oder Schadensersatz" },
                  { emoji: "🏛️", label: "Einigungsstelle", desc: "Neutrale Schlichtung zwischen BR und Arbeitgeber" },
                  { emoji: "🤝", label: "Gewerkschaft", desc: "Rechtsschutz und Beratung durch ver.di, IG Metall & Co." },
                ].map((item, i) => (
                  <div key={i} className="bg-gray-50 rounded-xl p-4 text-center">
                    <div className="text-2xl mb-2">{item.emoji}</div>
                    <div className="font-bold text-[#2D3748] text-sm mb-1">{item.label}</div>
                    <div className="text-[#A0AEC0] text-xs">{item.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-br from-[#E6FFFA] to-[#EBF8FF] rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-3" style={{ fontFamily: 'Nunito' }}>
              Jetzt dokumentieren — bevor du zum Betriebsrat gehst
            </h2>
            <p className="text-[#718096] mb-6 max-w-xl mx-auto">
              Mit einer soliden Dokumentation wirst du beim Betriebsrat ernst genommen. Erstelle dein Protokoll anonym und kostenlos.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/melden" className="inline-flex items-center justify-center gap-2 bg-[#A0E8AF] text-[#1A4731] font-bold px-8 py-4 rounded-full hover:bg-[#86D696] transition-all">
                <FileText className="w-5 h-5" />
                Protokoll erstellen
              </Link>
              <Link to="/anwalt" className="inline-flex items-center justify-center gap-2 border-2 border-[#BDE0FE] text-[#1A365D] font-bold px-8 py-4 rounded-full hover:bg-[#BDE0FE]/20 transition-all">
                Rechtliche Schritte →
              </Link>
            </div>
          </div>

        </div>
      </main>
      <Footer />
    </div>
  );
}
